import { useState } from "react";
import { Link, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { GoogleLogo, Envelope, Lock } from "@phosphor-icons/react";

export default function Login() {
  const { user, loading, login, beginGoogle } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (loading) {
    return (
      <div className="min-h-screen ledger-paper flex items-center justify-center">
        <p className="font-mono-ledger text-xs text-[#5C5F58] uppercase tracking-[0.25em]">Opening the vault…</p>
      </div>
    );
  }
  const next = searchParams.get("next") || location.state?.from || "/";
  if (user) return <Navigate to={next} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (!res.ok) setError(res.error);
    else toast.success("Welcome back.");
  };

  return (
    <AuthShell>
      <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
        Registered Ledger Holders
      </div>
      <h1 className="font-serif-display text-4xl text-[#0A192F] leading-tight mb-1">Sign in.</h1>
      <p className="text-sm text-[#5C5F58] mb-6">
        Access your standing estate manifest.
      </p>

      <button
        onClick={beginGoogle}
        data-testid="google-login-btn"
        className="w-full inline-flex items-center justify-center gap-3 px-4 py-3 text-sm border border-[#0A192F] bg-white hover:bg-[#0A192F] hover:text-[#F9F8F6] transition-colors mb-6"
      >
        <GoogleLogo size={16} weight="bold" />
        Continue with Google
      </button>

      <div className="flex items-center gap-3 text-[10px] font-mono-ledger uppercase tracking-[0.25em] text-[#5C5F58] mb-6">
        <span className="h-px flex-1 bg-[#C9C4B8]" />
        or email
        <span className="h-px flex-1 bg-[#C9C4B8]" />
      </div>

      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Field icon={<Envelope size={14} />} label="Email">
          <input
            data-testid="login-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-white border border-[#E0DDD6] pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#1A3622]"
          />
        </Field>
        <Field icon={<Lock size={14} />} label="Password">
          <input
            data-testid="login-password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-white border border-[#E0DDD6] pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#1A3622]"
          />
        </Field>

        <div className="flex justify-end -mt-2">
          <Link
            to="/forgot-password"
            data-testid="link-forgot-password"
            className="text-xs text-[#5C5F58] hover:text-[#1A3622] font-mono-ledger uppercase tracking-[0.2em]"
          >
            Forgot password?
          </Link>
        </div>

        {error && (
          <div className="text-xs text-[#6B1718] bg-[#6B1718]/5 border border-[#6B1718]/30 px-3 py-2" data-testid="login-error">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          data-testid="login-submit"
          className="w-full bg-[#1A3622] text-[#F9F8F6] py-3 text-sm font-medium border border-[#0F2818] hover:bg-[#0F2818] disabled:opacity-60"
        >
          {submitting ? "Signing in…" : "Sign in"}
        </button>
      </form>

      <p className="text-xs text-[#5C5F58] mt-6 text-center">
        No ledger yet?{" "}
        <Link
          to={searchParams.get("next") ? `/register?next=${encodeURIComponent(searchParams.get("next"))}` : "/register"}
          className="underline underline-offset-4 text-[#1A3622]"
          data-testid="link-register"
        >
          Register
        </Link>
      </p>
    </AuthShell>
  );
}

export function Field({ icon, label, children }) {
  return (
    <label className="block">
      <span className="block font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-1.5">{label}</span>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5F58]">{icon}</span>
        {children}
      </div>
    </label>
  );
}

export function AuthShell({ children }) {
  return (
    <div className="min-h-screen ledger-paper flex flex-col" data-testid="auth-shell">
      <header className="px-6 md:px-10 py-6 border-b border-[#C9C4B8]">
        <Link to="/" className="inline-flex items-center gap-3">
          <img
            src="/logo-mark.png"
            alt="The Asset Arbor"
            className="w-10 h-10 object-contain shrink-0"
            draggable="false"
          />
          <div className="leading-none">
            <div className="font-serif-display text-2xl text-[#0A192F]">The Asset Arbor</div>
            <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
              Est. 2026 · A Generational Ledger
            </div>
          </div>
        </Link>
      </header>
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="bg-white border border-[#E0DDD6] p-8 fade-in-up">
            {children}
          </div>
          <p className="font-serif-display italic text-sm text-[#5C5F58] text-center mt-6">
            A generational ledger for optimized ownership and a secure legacy.
          </p>
        </div>
      </main>
    </div>
  );
}
